/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useEffect, useRef, useState } from "react"
import { ThumbsDownOutlined, ThumbsUpOutlined } from "@duxton-web/icons"
import {
  Button,
  Checkbox,
  Grid,
  Input,
  Radio,
  Typography,
} from "@duxton-web/ui"
import classNames from "classnames"

import { translationOrFallback, useTranslationsMap } from "common/utils/i18n"

import {
  isPmfFeedbackFeatureEvent,
  PMF_FEEDBACK_EVENT,
  PMF_FEEDBACK_FEATURE_CHOICES,
  PMF_FEEDBACK_FEATURE_FALLBACKS,
  PMF_FEEDBACK_FEATURE_I18N_PATHS,
  PMF_FEEDBACK_OPTIONS,
  PMF_FEEDBACK_RATING,
  PMF_FEEDBACK_SELECTION_FALLBACKS,
  PMF_FEEDBACK_SELECTION_I18N_PATHS,
  PmfFeedbackEvent,
  PmfFeedbackFeatureChoice,
  PmfFeedbackRating,
  PmfFeedbackSelection,
} from "./pmfFeedbackOptions"
import styles from "./PmfFeedbackContent.modules.css"

const RATINGS = Object.values(PMF_FEEDBACK_RATING)

const scrollCommentIntoView = (commentInput: HTMLTextAreaElement | null) => {
  if (!commentInput || document.activeElement !== commentInput) return

  const visualViewport = window.visualViewport
  const visibleBottom = visualViewport
    ? visualViewport.offsetTop + visualViewport.height
    : window.innerHeight
  if (commentInput.getBoundingClientRect().bottom <= visibleBottom) return

  commentInput.scrollIntoView({
    behavior: "auto",
    block: "center",
    inline: "nearest",
  })
}

const RATING_ICONS: Record<PmfFeedbackRating, React.ReactElement> = {
  [PMF_FEEDBACK_RATING.GOOD]: <ThumbsUpOutlined aria-hidden />,
  [PMF_FEEDBACK_RATING.BAD]: <ThumbsDownOutlined aria-hidden />,
}

/**
 * TEMPORARY (BP-63014) — English fallbacks for headings/links whose PhraseApp
 * keys exist and are mapped in `scripts/phraseapp/phraseapp_config.json` but are
 * not published yet, so `translate` resolves them to "" and they would render
 * blank. `translationOrFallback` hands over automatically once they are
 * non-empty.
 *
 * TODO(BP-63014): delete these constants and read `textMap.*` directly once the
 * copy is published. Confirm with `yarn check-missing-locales:fresh --strict`.
 */
const OTHER_SUGGESTIONS_FALLBACK = "Other suggestions"
const TELL_US_MORE_FALLBACK = "Tell us more"
const SHOW_MORE_FALLBACK = "Show more"

export interface PmfFeedbackPayload {
  rating: PmfFeedbackRating
  topics: PmfFeedbackSelection[]
  comment: string
  /** GENERAL only — the feature the merchant picked before the topic options. */
  feature?: PmfFeedbackFeatureChoice
}

export interface PmfFeedbackState {
  rating: PmfFeedbackRating | null
  topics: PmfFeedbackSelection[]
  comment: string
  feature: PmfFeedbackFeatureChoice | null
}

interface PmfFeedbackContentProps {
  /** Which flow triggered the prompt — drives the option list. Defaults to GENERAL. */
  event?: PmfFeedbackEvent
  reserveCloseButtonSpace?: boolean
  onRatingChange?: (rating: PmfFeedbackRating) => void
  onStateChange?: (state: PmfFeedbackState) => void
  onSubmit?: (payload: PmfFeedbackPayload) => void
}

export const PmfFeedbackContent: FC<PmfFeedbackContentProps> = ({
  event = PMF_FEEDBACK_EVENT.GENERAL,
  reserveCloseButtonSpace = false,
  onRatingChange,
  onStateChange,
  onSubmit,
}) => {
  const [rating, setRating] = useState<PmfFeedbackRating | null>(null)
  const [topics, setTopics] = useState<PmfFeedbackSelection[]>([])
  const [comment, setComment] = useState("")
  const [feature, setFeature] = useState<PmfFeedbackFeatureChoice | null>(null)
  const [isFeatureListExpanded, setIsFeatureListExpanded] = useState(false)
  const commentInputRef = useRef<HTMLTextAreaElement | null>(null)

  const { md } = Grid.useBreakpoint()
  const isExpanded = rating !== null
  const showHeaderHeading = md !== false || !isExpanded
  const isGeneral = event === PMF_FEEDBACK_EVENT.GENERAL
  // "Others" is a feature choice with no topic options, so it shows free text only.
  const showsTopics = !!feature && isPmfFeedbackFeatureEvent(feature)

  useEffect(() => {
    onStateChange?.({ rating, topics, comment, feature })
  }, [rating, topics, comment, feature, onStateChange])

  useEffect(() => {
    if (md !== false) return undefined

    const visualViewport = window.visualViewport
    const revealFocusedComment = () =>
      scrollCommentIntoView(commentInputRef.current)

    window.addEventListener("resize", revealFocusedComment)
    visualViewport?.addEventListener("resize", revealFocusedComment)

    return () => {
      window.removeEventListener("resize", revealFocusedComment)
      visualViewport?.removeEventListener("resize", revealFocusedComment)
    }
  }, [md])

  const textMap = useTranslationsMap({
    heading: "ecommerce.sellerCentre.pmfFeedback.heading",
    good: "ecommerce.sellerCentre.pmfFeedback.good",
    bad: "ecommerce.sellerCentre.pmfFeedback.bad",
    whatDidYouLike: "ecommerce.sellerCentre.pmfFeedback.whatDidYouLike",
    whatCanWeImprove: "ecommerce.sellerCentre.pmfFeedback.whatCanWeImprove",
    selectMoreThanOne: "ecommerce.sellerCentre.pmfFeedback.selectMoreThanOne",
    tellUsMore: "ecommerce.sellerCentre.pmfFeedback.tellUsMore",
    showMore: "ecommerce.sellerCentre.pmfFeedback.showMore",
    otherSuggestions: "ecommerce.sellerCentre.pmfFeedback.otherSuggestions",
    commentPlaceholder: "ecommerce.sellerCentre.pmfFeedback.commentPlaceholder",
    submit: "ecommerce.sellerCentre.pmfFeedback.submit",
  })

  const selectionLabels = useTranslationsMap(
    PMF_FEEDBACK_SELECTION_I18N_PATHS as Record<string, string>,
  )

  const featureLabels = useTranslationsMap(
    PMF_FEEDBACK_FEATURE_I18N_PATHS as Record<string, string>,
  )

  const RATING_LABELS: Record<PmfFeedbackRating, string> = {
    [PMF_FEEDBACK_RATING.GOOD]: textMap.good,
    [PMF_FEEDBACK_RATING.BAD]: textMap.bad,
  }

  const TOPIC_SECTION_HEADINGS: Record<PmfFeedbackRating, string> = {
    [PMF_FEEDBACK_RATING.GOOD]: textMap.whatDidYouLike,
    [PMF_FEEDBACK_RATING.BAD]: textMap.whatCanWeImprove,
  }

  // A feature event has a static list for its own rating; GENERAL — the only
  // other event — resolves topic options from the feature the merchant picked.
  const getTopicOptions = (): PmfFeedbackSelection[] => {
    if (!rating) return []
    if (isPmfFeedbackFeatureEvent(event)) {
      return PMF_FEEDBACK_OPTIONS[event][rating]
    }
    return feature && isPmfFeedbackFeatureEvent(feature)
      ? PMF_FEEDBACK_OPTIONS[feature][rating]
      : []
  }
  const topicOptions = getTopicOptions()

  // Once a feature is picked the list collapses to it, with "Show more" to reopen.
  const visibleFeatureChoices: PmfFeedbackFeatureChoice[] =
    feature && !isFeatureListExpanded ? [feature] : PMF_FEEDBACK_FEATURE_CHOICES

  const toggleTopic = (topic: PmfFeedbackSelection) => {
    setTopics(current =>
      current.includes(topic)
        ? current.filter(item => item !== topic)
        : [...current, topic],
    )
  }

  const isSubmitDisabled = topics.length === 0 && comment.trim().length === 0

  const handleSubmit = () => {
    if (!rating || isSubmitDisabled) return
    onSubmit?.({
      rating,
      topics,
      comment,
      ...(isGeneral && feature ? { feature } : {}),
    })
  }

  const handleFeatureSelect = (value: PmfFeedbackFeatureChoice) => {
    // Topic options belong to the feature, so switching feature invalidates them.
    if (value !== feature) setTopics([])
    setFeature(value)
    setIsFeatureListExpanded(false)
  }

  const handleRatingSelect = (value: PmfFeedbackRating) => {
    // GOOD and BAD show different options, so switching rating invalidates the
    // current selection — but re-clicking the same rating must keep it.
    if (value !== rating) {
      setTopics([])
      setFeature(null)
      setIsFeatureListExpanded(false)
      // GENERAL resets the whole form on a rating change — the free text too.
      if (isGeneral) setComment("")
    }
    setRating(value)
    onRatingChange?.(value)
  }

  return (
    <>
      <div
        className={classNames(styles.header, {
          [styles.headerWithCloseButton]: reserveCloseButtonSpace,
        })}
      >
        {showHeaderHeading && (
          <Typography.Body level={1} strong className={styles.title}>
            {textMap.heading}
          </Typography.Body>
        )}
        <div className={styles.ratings}>
          {RATINGS.map(value => {
            const isSelected = rating === value
            return (
              <Button
                key={value}
                size="large"
                type="secondary"
                icon={RATING_ICONS[value]}
                className={classNames(styles.ratingButton, {
                  [styles.ratingButtonSelected]: isSelected,
                })}
                onClick={() => handleRatingSelect(value)}
                aria-pressed={isSelected}
              >
                {RATING_LABELS[value]}
              </Button>
            )
          })}
        </div>
      </div>
      {isExpanded && (
        <div className={styles.form}>
          <div className={styles.formIntro}>
            <Typography.Body level={1} strong className={styles.title}>
              {TOPIC_SECTION_HEADINGS[rating]}
            </Typography.Body>
            {/* GENERAL picks a single feature here, so "select more than one"
                moves down to the topic options section instead. */}
            {!isGeneral && (
              <Typography.Metadata level={1}>
                {textMap.selectMoreThanOne}
              </Typography.Metadata>
            )}
          </div>
          {isGeneral && (
            <div className={styles.featureSection}>
              {/*
                `Radio` renders its own `<label>`, so it owns the row — do not wrap
                it. A checked radio fires no `change`, but the only re-select case
                (tapping the already-picked radio) is an intentional no-op here.
              */}
              <Radio.Group
                className={styles.features}
                value={feature}
                onChange={radioEvent =>
                  handleFeatureSelect(
                    radioEvent.target.value as PmfFeedbackFeatureChoice,
                  )
                }
              >
                {visibleFeatureChoices.map(choice => (
                  <Radio key={choice} value={choice} className={styles.feature}>
                    {translationOrFallback(
                      featureLabels[choice],
                      PMF_FEEDBACK_FEATURE_FALLBACKS[choice],
                    )}
                  </Radio>
                ))}
              </Radio.Group>
              {!!feature && !isFeatureListExpanded && (
                <Button
                  type="link"
                  className={styles.showMore}
                  onClick={() => setIsFeatureListExpanded(true)}
                >
                  {translationOrFallback(textMap.showMore, SHOW_MORE_FALLBACK)}
                </Button>
              )}
            </div>
          )}
          {(!isGeneral || showsTopics) && (
            <>
              {isGeneral && (
                <div className={styles.formIntro}>
                  <Typography.Body level={1} strong className={styles.title}>
                    {translationOrFallback(
                      textMap.tellUsMore,
                      TELL_US_MORE_FALLBACK,
                    )}
                  </Typography.Body>
                  <Typography.Metadata level={1}>
                    {textMap.selectMoreThanOne}
                  </Typography.Metadata>
                </div>
              )}
              <div className={styles.topics}>
                {topicOptions.map(option => (
                  <Checkbox
                    key={option}
                    checked={topics.includes(option)}
                    onChange={() => toggleTopic(option)}
                    className={styles.topic}
                  >
                    {translationOrFallback(
                      selectionLabels[option],
                      PMF_FEEDBACK_SELECTION_FALLBACKS[option],
                    )}
                  </Checkbox>
                ))}
              </div>
            </>
          )}
          {(!isGeneral || !!feature) && (
            <div className={styles.otherSuggestions}>
              <Typography.Body level={1} strong className={styles.title}>
                {translationOrFallback(
                  textMap.otherSuggestions,
                  OTHER_SUGGESTIONS_FALLBACK,
                )}
              </Typography.Body>
              <Input.TextArea
                value={comment}
                onChange={inputEvent => setComment(inputEvent.target.value)}
                placeholder={textMap.commentPlaceholder}
                autoComplete="off"
                className={styles.comment}
                maxLength={500}
                showCount
                autoSize={{ minRows: 2, maxRows: 5 }}
                onFocus={inputEvent => {
                  if (md !== false) return

                  commentInputRef.current = inputEvent.currentTarget
                  scrollCommentIntoView(commentInputRef.current)
                }}
                onBlur={() => {
                  commentInputRef.current = null
                }}
              />
            </div>
          )}
          <Button
            type="primary"
            block
            size="large"
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
          >
            {textMap.submit}
          </Button>
        </div>
      )}
    </>
  )
}
